import {
  Box,
  Flex,
  HStack,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Progress,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  useGetSurveyGroupByIdQuery,
} from '../../generated/graphql';
import { HelivanLoading } from '../HelivanLoading';
import { IoArrowBackOutline } from 'react-icons/io5';
import { RiAddCircleLine } from 'react-icons/ri';

export const SurveyGroupView = () => {
  const { groupId } = useParams();
  const { data, loading } = useGetSurveyGroupByIdQuery({
    variables: { id: groupId || '' },
  });
  const surveyGroup = data?.getSurveyGroupById;
  const navigate = useNavigate();

  // Calculate demographic breakdowns
  const calculateDemographics = () => {
    const digitalTwins = surveyGroup?.digitalTwins || [];
    const total = digitalTwins.length;

    if (total === 0) {
      return { genderBreakdown: [], ageBreakdown: [], ethnicityBreakdown: [] };
    }

    // Count demographics
    const genderCounts: Record<string, number> = {};
    const ageCounts: Record<string, number> = {};
    const ethnicityCounts: Record<string, number> = {};

    digitalTwins.forEach((dt: any) => {
      const userData = dt.surveyUserData;
      if (userData?.gender) {
        genderCounts[userData.gender] =
          (genderCounts[userData.gender] || 0) + 1;
      }
      if (userData?.age) {
        ageCounts[userData.age] = (ageCounts[userData.age] || 0) + 1;
      }
      if (userData?.ethnicity) {
        ethnicityCounts[userData.ethnicity] =
          (ethnicityCounts[userData.ethnicity] || 0) + 1;
      }
    });

    // Convert to percentages
    const toPercentages = (counts: Record<string, number>) =>
      Object.entries(counts)
        .map(([key, count]) => ({
          label: key,
          count,
          percentage: ((count / total) * 100).toFixed(1),
        }))
        .sort((a, b) => b.count - a.count);

    return {
      genderBreakdown: toPercentages(genderCounts),
      ageBreakdown: toPercentages(ageCounts),
      ethnicityBreakdown: toPercentages(ethnicityCounts),
      total,
    };
  };

  const demographics = calculateDemographics();

  return loading ? (
    <HelivanLoading />
  ) : (
    <Stack
      width="full"
      height="full"
      spacing={0}
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
      overflow="hidden"
    >
      <Flex
        width="full"
        px="30px"
        py="16px"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        borderTopRadius="12px"
        borderBottomWidth="1px"
        justifyContent="space-between"
        alignItems="center"
      >
        <HStack spacing={2}>
          <Box
            onClick={() => navigate('/surveys?people=true')}
            _hover={{ cursor: 'pointer' }}
          >
            <IoArrowBackOutline size={16} />
          </Box>
          <HelivanText variant="largeSubheader">
            {data?.getSurveyGroupById?.name}
          </HelivanText>
        </HStack>
        <HelivanButton
          size="sm"
          variant="green"
          onClick={() =>
            navigate('/surveys/create-new?defaultGroupId=' + surveyGroup?.id)
          }
        >
          <HStack spacing={1}>
            <RiAddCircleLine color="#245B4A" size={16} />
            <HelivanText color="#245B4A" variant="largeBody+">
              Create Survey for this group
            </HelivanText>
          </HStack>
        </HelivanButton>
      </Flex>

      <Box overflowY="auto">
        <Tabs variant="line" colorScheme="blue" width="full">
          <TabList width="full" justifyContent="center">
            <Tab
              _selected={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
              _hover={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
            >
              Personas
            </Tab>
            <Tab
              _selected={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
              _hover={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
            >
              Surveys
            </Tab>
          </TabList>
          <TabPanels width="full" height="full">
            <TabPanel width="full" height="full">
              <Stack spacing={6} width="full" p={4}>
                <Flex justifyContent="space-between" alignItems="center">
                  <HelivanText variant="largeBody+">
                    Demographics Overview
                  </HelivanText>
                  <Text fontSize="sm" color="gray.600">
                    Total Personas: {demographics.total}
                  </Text>
                </Flex>

                {demographics.total === 0 ? (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No personas in this group yet.
                  </Text>
                ) : (
                  <>
                    {/* Gender Breakdown */}
                    {demographics.genderBreakdown.length > 0 && (
                      <Card variant="outline">
                        <CardHeader>
                          <Heading size="sm">Gender</Heading>
                        </CardHeader>
                        <CardBody>
                          <Stack spacing={3}>
                            {demographics.genderBreakdown.map((item) => (
                              <Box key={item.label}>
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.label}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.percentage}% ({item.count})
                                  </Text>
                                </Flex>
                                <Progress
                                  value={parseFloat(item.percentage)}
                                  colorScheme="blue"
                                  size="sm"
                                  borderRadius="md"
                                />
                              </Box>
                            ))}
                          </Stack>
                        </CardBody>
                      </Card>
                    )}

                    {/* Age Breakdown */}
                    {demographics.ageBreakdown.length > 0 && (
                      <Card variant="outline">
                        <CardHeader>
                          <Heading size="sm">Age</Heading>
                        </CardHeader>
                        <CardBody>
                          <Stack spacing={3}>
                            {demographics.ageBreakdown.map((item) => (
                              <Box key={item.label}>
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.label}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.percentage}% ({item.count})
                                  </Text>
                                </Flex>
                                <Progress
                                  value={parseFloat(item.percentage)}
                                  colorScheme="green"
                                  size="sm"
                                  borderRadius="md"
                                />
                              </Box>
                            ))}
                          </Stack>
                        </CardBody>
                      </Card>
                    )}

                    {/* Ethnicity Breakdown */}
                    {demographics.ethnicityBreakdown.length > 0 && (
                      <Card variant="outline">
                        <CardHeader>
                          <Heading size="sm">Ethnicity</Heading>
                        </CardHeader>
                        <CardBody>
                          <Stack spacing={3}>
                            {demographics.ethnicityBreakdown.map((item) => (
                              <Box key={item.label}>
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.label}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {item.percentage}% ({item.count})
                                  </Text>
                                </Flex>
                                <Progress
                                  value={parseFloat(item.percentage)}
                                  colorScheme="purple"
                                  size="sm"
                                  borderRadius="md"
                                />
                              </Box>
                            ))}
                          </Stack>
                        </CardBody>
                      </Card>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
            <TabPanel>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="full"
                py={2}
                px={6}
                borderBottomWidth="1px"
                borderColor={HelivanColor.border.primary}
                borderTopWidth="1px"
                bg="#F7F8F9"
              >
                <HelivanText variant="largeBody+">
                  Surveys taken by this group
                </HelivanText>
              </Flex>
              {surveyGroup?.surveys?.map((survey) => (
                <Flex
                  key={survey.id}
                  justifyContent="space-between"
                  alignItems="center"
                  width="full"
                  py={2}
                  px={6}
                  borderBottomWidth="1px"
                  bg="white"
                  cursor="pointer"
                  _hover={{
                    bg: HelivanColor.background.secondary,
                  }}
                  onClick={() => navigate(`/surveys/${survey.id}`)}
                >
                  <HelivanText variant="largeBody" fontWeightOverride={500}>
                    {survey.title}
                  </HelivanText>
                </Flex>
              ))}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Stack>
  );
};
